import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import pull from 'lodash/pull';

export function filterTreeShallow(node, searchValue, predicate) {
  if (predicate(node, searchValue)) return node;
  if (!node.children) return null;

  const matchedChildren = node.children
    .map((child) => filterTreeShallow(child, searchValue, predicate))
    .filter(Boolean);

  if (matchedChildren.length === 0) return null;
  return {...node, children: matchedChildren};
}

export function getAllDescendantIds(nodeId, nodes) {
  const ids = [];

  function traverse(children) {
    if (!children) return;

    for (const child of children) {
      ids.push(child.id);
      traverse(child.children);
    }
  }

  function findAndTraverse(nodes) {
    for (const node of nodes) {
      if (node.id === nodeId) {
        traverse(node.children);

        return true;
      }

      if (node.children && findAndTraverse(node.children)) return true;
    }
    return false;
  }

  findAndTraverse(nodes);
  return ids;
}

/**
 * Returns the IDs of every ancestor of the given node, ordered from the root down to its immediate parent.
 *
 * @param {number|string} nodeId - ID of the node whose ancestors are wanted
 * @param {Array} nodes - Array of root nodes, each with `id`, `children` fields
 * @returns {Array} Ancestor IDs, or an empty array when the node is a root or is not found
 */
export function getAncestorIds(nodeId, nodes) {
  function findPath(siblings, path) {
    if (!siblings) return null;

    for (const node of siblings) {
      if (node.id === nodeId) return path;

      const found = findPath(node.children, [...path, node.id]);

      if (found) return found;
    }
    return null;
  }

  return findPath(nodes, []) ?? [];
}

export function collectAllKeys(nodes, acc = []) {
  for (const node of nodes) {
    acc.push(node.id);
    if (node.children) collectAllKeys(node.children, acc);
  }
  return acc;
}

/**
 * Case-insensitive name match predicate for use with filterTreeShallow.
 * Matches nodes whose `name` field contains the search value.
 *
 * @param {Object} node - Tree node with a `name` property
 * @param {string} searchValue - Search term
 * @returns {boolean}
 */
export function nameMatchPredicate(node, searchValue) {
  const name = node?.name ?? '';
  return name.toLowerCase().includes(searchValue.toLowerCase());
}

/**
 * Computes half-checked and derived-checked keys for a tree given a set of explicitly checked IDs.
 *
 * - halfCheckedKeys: nodes where some but not all descendants are checked (indeterminate state)
 * - derivedCheckedKeys: nodes where all descendants are checked but the node itself is not explicitly checked
 *
 * Use with checkStrictly={true} on AntD Tree. Pass derivedCheckedKeys into the checked array
 * and halfCheckedKeys into the halfChecked array, filtering out any halfChecked IDs from checked.
 *
 * @param {Array} treeData - Array of root nodes, each with `id`, `children` fields
 * @param {Array} checkedIds - Array of explicitly checked node IDs
 * @returns {{halfCheckedKeys: Array, derivedCheckedKeys: Array}}
 */
export function computeTreeCheckedState(treeData, checkedIds) {
  if (checkedIds.length === 0) return {halfCheckedKeys: [], derivedCheckedKeys: []};
  const checkedSet = new Set(checkedIds);
  const halfResult = [];
  const derivedResult = [];

  const walk = (node) => {
    const selfChecked = checkedSet.has(node.id);

    if (!node.children || node.children.length === 0) {
      return {anyChecked: selfChecked, allChecked: selfChecked};
    }

    const childResults = node.children.map(walk);
    const anyChildChecked = childResults.some((r) => r.anyChecked);
    const allChildrenChecked = childResults.every((r) => r.allChecked);

    if (anyChildChecked && !allChildrenChecked) {
      halfResult.push(node.id);
    } else if (!selfChecked && allChildrenChecked) {
      derivedResult.push(node.id);
    }

    return {anyChecked: selfChecked || anyChildChecked, allChecked: selfChecked || allChildrenChecked};
  };

  treeData.forEach(walk);

  return {halfCheckedKeys: halfResult, derivedCheckedKeys: derivedResult};
}

/**
 * Filters tree nodes but includes ALL descendants when a parent matches.
 * If a node matches the predicate, all of its children/descendants are included regardless of match.
 * Preserves ancestor paths - if a child matches, all its parents are included.
 *
 * Use this for filter UIs where you want to show full context under matching parents.
 *
 * @param {Object} tree - Root node of the tree to filter
 * @param {string} searchValue - Search term to filter by
 * @param {Function} matchPredicate - Function to determine if a node matches: (node, searchValue) => boolean
 * @returns {Object} Filtered tree with matching nodes and ALL their descendants, or empty object if no matches
 *
 */
export function filterTreeWithDescendants(tree, searchValue, matchPredicate) {
  if (isNil(searchValue) || (isString(searchValue) && searchValue.trim().length === 0)) {
    return tree;
  }

  const clonedTree = cloneDeep(tree);
  return _filterNode(clonedTree) ? clonedTree : {};

  function _filterNode(node) {
    if (!isNil(node)) {
      // Check if THIS node matches FIRST - if so, keep all descendants
      if (matchPredicate(node, searchValue)) {
        return true; // Keep this node and ALL its children as-is
      }

      // Node doesn't match - filter its children recursively
      if (Array.isArray(node.children)) {
        for (let i = 0; i < node.children.length; i++) {
          if (!_filterNode(node.children[i])) {
            node.children[i] = null;
          }
        }

        pull(node.children, null);

        if (node.children.length === 0) {
          node.children = null;
        }
      }

      return !isEmpty(node.children);
    }
    return false;
  }
}
